import { useState, useMemo, useEffect } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
} from 'date-fns';
import type { TimeSlot } from '../types/booking.types';

/** Timezone-safe date key (yyyy-MM-dd) honouring the booking timezone. */
function toDateKey(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: timezone,
  }).formatToParts(new Date(iso));
  const y = parts.find((p) => p.type === 'year')!.value;
  const m = parts.find((p) => p.type === 'month')!.value;
  const d = parts.find((p) => p.type === 'day')!.value;
  return `${y}-${m}-${d}`;
}

/** Date key for a local Date (calendar grid). */
export function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Calendar state for the booking flow — month navigation, the day grid,
 * slots-by-date and the slots for the selected day. Extracted so the page can
 * lay the calendar, time cards and report list out as separate rows.
 */
export function useBookingCalendar(slots: TimeSlot[], timezone: string) {
  const tz = timezone || 'Australia/Sydney';
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const slotsByDate = useMemo(() => {
    const map = new Map<string, TimeSlot[]>();
    for (const slot of slots) {
      const key = toDateKey(slot.start, tz);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(slot);
    }
    return map;
  }, [slots, tz]);

  // Auto-select the first available date.
  useEffect(() => {
    if (slots.length === 0 || selectedDate) return;
    const key = toDateKey(slots[0].start, tz);
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    setSelectedDate(date);
    setViewMonth(startOfMonth(date));
  }, [slots, tz, selectedDate]);

  const calendarDays = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [viewMonth]);

  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return [];
    return slotsByDate.get(localDateKey(selectedDate)) || [];
  }, [selectedDate, slotsByDate]);

  const timeFormatter = useMemo(
    () => new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz }),
    [tz],
  );
  const dayHeaderFormatter = useMemo(
    () => new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: 'numeric', month: 'long', timeZone: tz }),
    [tz],
  );

  return {
    tz,
    viewMonth,
    selectedDate,
    setSelectedDate,
    slotsByDate,
    calendarDays,
    selectedDateSlots,
    timeFormatter,
    dayHeaderFormatter,
    prevMonth: () => setViewMonth((m) => subMonths(m, 1)),
    nextMonth: () => setViewMonth((m) => addMonths(m, 1)),
  };
}
